import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Truck, Star, HelpCircle, Wrench, Image, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    motorhomes: 0,
    reviews: 0,
    faqs: 0,
    servicing: 0,
    gallery: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [motorhomes, reviews, faqs, servicing, gallery] = await Promise.all([
          axios.get(`${API_URL}/api/admin/motorhomes`, getAuthHeader()),
          axios.get(`${API_URL}/api/admin/reviews`, getAuthHeader()),
          axios.get(`${API_URL}/api/admin/faqs`, getAuthHeader()),
          axios.get(`${API_URL}/api/admin/servicing`, getAuthHeader()),
          axios.get(`${API_URL}/api/admin/gallery`, getAuthHeader())
        ]);
        setStats({
          motorhomes: motorhomes.data.length,
          reviews: reviews.data.length,
          faqs: faqs.data.length,
          servicing: servicing.data.length,
          gallery: gallery.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [getAuthHeader]);

  const statCards = [
    { label: 'Motorhomes', value: stats.motorhomes, icon: Truck, color: 'bg-blue-500', link: '/admin/motorhomes' },
    { label: 'Reviews', value: stats.reviews, icon: Star, color: 'bg-yellow-500', link: '/admin/reviews' },
    { label: 'FAQs', value: stats.faqs, icon: HelpCircle, color: 'bg-green-500', link: '/admin/faqs' },
    { label: 'Servicing Items', value: stats.servicing, icon: Wrench, color: 'bg-purple-500', link: '/admin/servicing' },
    { label: 'Gallery Images', value: stats.gallery, icon: Image, color: 'bg-pink-500', link: '/admin/gallery' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1">Welcome to your admin panel</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{stat.label}</p>
                      <p className="text-3xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <div className={`${stat.color} p-3 rounded-lg`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/motorhomes" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium">Manage Motorhomes</p>
              <p className="text-sm text-slate-600">Add, edit or remove motorhomes from your fleet</p>
            </Link>
            <Link to="/admin/reviews" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium">Manage Reviews</p>
              <p className="text-sm text-slate-600">Add new customer reviews or edit existing ones</p>
            </Link>
            <Link to="/admin/faqs" className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <p className="font-medium">Update FAQs</p>
              <p className="text-sm text-slate-600">Keep your frequently asked questions up to date</p>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 text-sm">
              Your admin panel is ready. Use the sidebar to navigate between different sections
              and manage your website content.
            </p>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">💡 Tip</p>
              <p className="text-sm text-blue-700 mt-1">
                Changes you make here will be reflected on your live website immediately.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
